'use client'

import { Fragment, useEffect, useRef, useState } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { EDOS_PAGE } from '@/utils/constants'
import PACKAGE_HELPER from '@/helper/packageHelper'
import TestAndPackagesCard from './TestAndPackagesCard'
import TestAndPackagesListSkeleton from './Skeleton/TestAndPackagesListSkeleton'
import CommonSearchInput from './CommonSearchInput'
import usePopularHealthCheckupsAndSearchProducts from '@/hooks/usePopularHealthCheckupsAndSearchProducts'

const HeaderSearchBar = () => {
  const { handleCheckValidationForProduct } = PACKAGE_HELPER()

  // Declared above the hook: it feeds loadPopular, so the popover drives the fetch.
  const [isOpen, setIsOpen] = useState(false)

  const {
    pathname,
    packageState,
    mostPopularPackage: MOST_POPULAR_PACKAGE,
    mostPopularTest: MOST_POPULAR_TEST,
    searchQuery,
    setSearchQuery,
    searchQueryProductData,
    setSearchQueryProductData,
    loading,
    showFlag,
    setShowFlag,
    debounceSearch,
  } = usePopularHealthCheckupsAndSearchProducts({ loadPopular: isOpen })

  const searchInputRef = useRef<HTMLInputElement>(null)

  // Reset search when popover closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('')
      setSearchQueryProductData([])
      setShowFlag(false)
    }
  }, [isOpen])

  const handleCloseDropdown = () => {
    setIsOpen(false)
  }

  const PRODUCT_DATA_TO_MAP =
    (!searchQuery && searchQueryProductData?.length === 0
      ? [
          ...(packageState?.mostPopularPackage ?? []),
          ...(packageState?.mostPopularTest ?? []),
        ]
      : searchQueryProductData) || []

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="hidden lg:flex flex-1 max-w-[540px] mx-6 cursor-pointer">
          <CommonSearchInput
            ref={searchInputRef}
            value={isOpen ? searchQuery : undefined}
            onChange={
              isOpen ? (e) => setSearchQuery(e.target.value) : undefined
            }
            placeholder={
              pathname === EDOS_PAGE
                ? 'Search by test name / test code'
                : 'Search for lab tests'
            }
            showCyclicText={!isOpen}
            containerClassName="w-full"
          />
        </div>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[--radix-popover-trigger-width] max-w-[640px] p-0 rounded-xl border border-gray-200 shadow-elevation4 overflow-hidden"
        onOpenAutoFocus={(e) => {
          e.preventDefault()
          setTimeout(() => searchInputRef.current?.focus(), 0)
        }}
      >
        {/* Results area */}
        <div className="overflow-y-auto max-h-[420px] bg-white-100">
          {!loading && (
            <Fragment>
              {(!debounceSearch || debounceSearch.length < 3) && (
                <Fragment>
                  <div className="px-5 pt-3 pb-2 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                      <p className="text-label-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Popular Packages
                      </p>
                    </div>
                  </div>
                  <div>
                    {!!MOST_POPULAR_PACKAGE &&
                      MOST_POPULAR_PACKAGE?.length > 0 &&
                      MOST_POPULAR_PACKAGE.slice(0, 5)?.map(
                        (product: any, index: number) => {
                          const productData = product?.productDto
                          const validationResponse =
                            handleCheckValidationForProduct(product)
                          const validationErrorToMap: any = validationResponse
                          return (
                            <TestAndPackagesCard
                              key={index}
                              product={product}
                              productDto={productData}
                              onOpenChange={handleCloseDropdown}
                              validationErrorToMap={validationErrorToMap}
                              setShowFlag={setShowFlag}
                            />
                          )
                        }
                      )}
                  </div>

                  <div className="px-5 pt-3 pb-2 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
                      <p className="text-label-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Popular Tests
                      </p>
                    </div>
                  </div>
                  <div>
                    {!!MOST_POPULAR_TEST &&
                      MOST_POPULAR_TEST?.length > 0 &&
                      MOST_POPULAR_TEST?.slice(0, 5)?.map(
                        (product: any, index: number) => {
                          const productData = product?.productDto
                          const validationResponse =
                            handleCheckValidationForProduct(product)
                          const validationErrorToMap: any = validationResponse
                          return (
                            <TestAndPackagesCard
                              key={index}
                              product={product}
                              productDto={productData}
                              onOpenChange={handleCloseDropdown}
                              validationErrorToMap={validationErrorToMap}
                              setShowFlag={setShowFlag}
                            />
                          )
                        }
                      )}
                  </div>
                </Fragment>
              )}

              {!!debounceSearch && (
                <div>
                  {!!searchQueryProductData &&
                    searchQueryProductData?.length > 0 &&
                    searchQueryProductData?.map(
                      (product: any, index: number) => {
                        const productData = product?.productDto
                        const validationResponse =
                          handleCheckValidationForProduct(product)
                        const validationErrorToMap: any = validationResponse
                        return (
                          <TestAndPackagesCard
                            key={index}
                            product={product}
                            productDto={productData}
                            onOpenChange={handleCloseDropdown}
                            validationErrorToMap={validationErrorToMap}
                            setShowFlag={setShowFlag}
                          />
                        )
                      }
                    )}
                  {!!debounceSearch &&
                    debounceSearch.length >= 3 &&
                    PRODUCT_DATA_TO_MAP?.length === 0 && (
                      <div className="py-12 px-6 flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-400"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                            <path d="m8 11 6 0" />
                          </svg>
                        </div>
                        <p className="text-body-sm font-semibold text-gray-800">
                          No results found
                        </p>
                        <p className="text-body-xs text-gray-700 mt-1 max-w-[280px]">
                          We couldn&apos;t find anything for &quot;
                          {debounceSearch}&quot;. Try a different search term.
                        </p>
                      </div>
                    )}
                </div>
              )}
            </Fragment>
          )}
          {!!loading && <TestAndPackagesListSkeleton count={4} />}
        </div>

        {/* Adding for patient toast */}
        {showFlag && (
          <div className="bg-primary-50 py-2 text-center border-t border-primary-100">
            <p className="text-label-xs font-semibold text-primary-600">
              Adding for patient
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

export default HeaderSearchBar
